import { forwardRef, useEffect, useId, useImperativeHandle, useRef } from "react";
import { DEFAULT_TURNSTILE_SITE_KEY } from "../config.js";
import { loadTurnstile, TURNSTILE_MESSAGES } from "../lib/turnstile.js";

/**
 * The Turnstile widget. Mount it only where it is visible (see
 * lib/turnstile.js). `onToken(value)` gets each token ("" when one expires or
 * fails); `onError(message)` gets a sentence for the visitor. The ref's
 * `reset()` throws the current token away and asks for a fresh one — needed
 * before a retry, because a reused token reads to siteverify as
 * `timeout-or-duplicate`.
 */
const Turnstile = forwardRef(function Turnstile({ siteKey, action = "public-form", onToken, onError, className = "" }, ref) {
  const containerId = `epro-turnstile-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const widgetId = useRef(null);
  const callbacks = useRef({ onToken, onError });
  useEffect(() => {
    callbacks.current = { onToken, onError };
  });

  useImperativeHandle(ref, () => ({
    reset() {
      callbacks.current.onToken?.("");
      if (widgetId.current !== null && typeof window.turnstile?.reset === "function") {
        try {
          window.turnstile.reset(widgetId.current);
        } catch (error) {
          console.error("[Turnstile] Reset failed:", error);
        }
      }
    },
  }));

  useEffect(() => {
    let cancelled = false;
    loadTurnstile().then(
      () => {
        if (cancelled || widgetId.current !== null) return;
        try {
          const id = window.turnstile.render(`#${containerId}`, {
            sitekey: siteKey || DEFAULT_TURNSTILE_SITE_KEY,
            theme: "light",
            size: "flexible",
            appearance: "always",
            action,
            callback: (value) => callbacks.current.onToken?.(value || ""),
            "error-callback": (code) => {
              console.error("[Turnstile] Error:", code);
              callbacks.current.onToken?.("");
              callbacks.current.onError?.(TURNSTILE_MESSAGES.failed);
            },
            "expired-callback": () => callbacks.current.onToken?.(""),
            "timeout-callback": () => callbacks.current.onToken?.(""),
          });
          if (id === undefined || id === null) throw new Error("render-returned-no-widget-id");
          widgetId.current = id;
        } catch (error) {
          console.error("[Turnstile] Render exception:", error);
          callbacks.current.onError?.(TURNSTILE_MESSAGES.render);
        }
      },
      (error) => {
        console.error("[Turnstile] API unavailable:", error?.message);
        if (!cancelled) callbacks.current.onError?.(TURNSTILE_MESSAGES.unavailable);
      },
    );
    return () => {
      cancelled = true;
      if (widgetId.current !== null && typeof window.turnstile?.remove === "function") {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* already gone */
        }
      }
      widgetId.current = null;
    };
  }, [containerId, siteKey, action]);

  return <div id={containerId} className={className} />;
});

export default Turnstile;
